"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumNameGenerator = exports.NameGeneratorAnnotationSelectionMode = exports.NameGeneratorDesignatorType = exports.NameGeneratorNameType = void 0;
exports.channelAlphaSuffix = channelAlphaSuffix;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const sigma_engine_shared_2 = require("@cadstrom/sigma-engine-shared");
const AltiumAnnotationFileUtil_1 = require("../../annotations/AltiumAnnotationFileUtil");
const _ = require("lodash");
const PathUtils_1 = require("../../common/PathUtils");
function channelAlphaSuffix(oneBasedIndex) {
    let n = oneBasedIndex;
    let suffix = '';
    while (n > 0) {
        const remainder = (n - 1) % 26;
        suffix = String.fromCharCode('A'.charCodeAt(0) + remainder) + suffix;
        n = Math.floor((n - 1) / 26);
    }
    return suffix;
}
var NameGeneratorNameType;
(function (NameGeneratorNameType) {
    NameGeneratorNameType[NameGeneratorNameType["Net"] = 0] = "Net";
    NameGeneratorNameType[NameGeneratorNameType["Module"] = 1] = "Module";
})(NameGeneratorNameType || (exports.NameGeneratorNameType = NameGeneratorNameType = {}));
var NameGeneratorDesignatorType;
(function (NameGeneratorDesignatorType) {
    NameGeneratorDesignatorType[NameGeneratorDesignatorType["Physical"] = 0] = "Physical";
    NameGeneratorDesignatorType[NameGeneratorDesignatorType["PhysicalInSchematic"] = 1] = "PhysicalInSchematic";
    NameGeneratorDesignatorType[NameGeneratorDesignatorType["Logical"] = 2] = "Logical";
})(NameGeneratorDesignatorType || (exports.NameGeneratorDesignatorType = NameGeneratorDesignatorType = {}));
var NameGeneratorAnnotationSelectionMode;
(function (NameGeneratorAnnotationSelectionMode) {
    NameGeneratorAnnotationSelectionMode[NameGeneratorAnnotationSelectionMode["ExplicitOnly"] = 0] = "ExplicitOnly";
    NameGeneratorAnnotationSelectionMode[NameGeneratorAnnotationSelectionMode["ExplicitOrChannel"] = 1] = "ExplicitOrChannel";
    NameGeneratorAnnotationSelectionMode[NameGeneratorAnnotationSelectionMode["ExplicitFirstUnconsumedChannel"] = 2] = "ExplicitFirstUnconsumedChannel";
})(NameGeneratorAnnotationSelectionMode || (exports.NameGeneratorAnnotationSelectionMode = NameGeneratorAnnotationSelectionMode = {}));
class AltiumNameGenerator {
    constructor(model, parsedDef, projectConfigUtil, annotationUtil, processingResultSink) {
        this.model = model;
        this.parsedDef = parsedDef;
        this.projectConfigUtil = projectConfigUtil;
        this.processingResultSink = processingResultSink;
        this.annotations = [];
        this.processingResults = [];
        this.refToNameMap = new Map();
        this.physicalNameToRefMap = new Map();
        this.physicalAnnotationClaims = new Map();
        this.ambiguousPhysicalAnnotationClaims = new Map();
        this.rejectedIndexedAnnotationIdentityMismatches = new Set();
        this.rejectedIndexedAnnotationIdentityMismatchExamples = [];
        this.claimedAnnotations = new Map();
        this.uniqueIdClaimedAnnotations = new Map();
        this.annotationUtil = annotationUtil ?? new AltiumAnnotationFileUtil_1.AltiumAnnotationFileUtil(parsedDef, projectConfigUtil);
        this.annotations = [...this.annotationUtil.allAnnotations];
    }
    get allAnnotations() {
        return this.annotations;
    }
    get numAnnotations() {
        return this.annotations.length;
    }
    getAnnotationEntryForPart(sheet, variables, enableFallbacks = false) {
        let matchingAnnotations = [];
        const logicalPart = sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variables, 'Component', true);
        const uniqueId = sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variables, 'UniqueID', true);
        const partId = sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variables, 'PartId')?.toString() ?? '1';
        const completeUniqueId = (0, PathUtils_1.joinPathUniqueIds)(sheet.pathUniqueId ?? '', uniqueId);
        const claimedAnnotation = this.uniqueIdClaimedAnnotations.get(completeUniqueId);
        if (claimedAnnotation !== undefined &&
            this.matchesPartIdentity(claimedAnnotation, sheet, logicalPart, partId, completeUniqueId)) {
            this.reportLogicalLabelDifference(claimedAnnotation, logicalPart, completeUniqueId);
            return claimedAnnotation;
        }
        matchingAnnotations = this.annotationUtil.getAnnotationsByUniqueId(completeUniqueId);
        if (matchingAnnotations.length === 0 && enableFallbacks) {
            if (logicalPart) {
                const allForDesignator = this.annotationUtil.getAnnotationsByLogicalReferenceDesignator(logicalPart);
                if (matchingAnnotations.length === 0) {
                    matchingAnnotations = allForDesignator.filter((a) => a.LogicalDesignator === logicalPart &&
                        a.UniqueID.endsWith(`\\${uniqueId}`) &&
                        a.DocumentName === sheet.filename &&
                        a.ChannelName === sheet.roomName &&
                        partId === a.LogicalPartID);
                }
                if (matchingAnnotations.length === 0) {
                    matchingAnnotations = allForDesignator.filter((a) => a.LogicalDesignator === logicalPart &&
                        a.UniqueID.endsWith(`\\${uniqueId}`) &&
                        a.DocumentName === sheet.filename &&
                        partId === a.LogicalPartID);
                }
                if (matchingAnnotations.length === 0) {
                    matchingAnnotations = allForDesignator.filter((a) => a.LogicalDesignator === logicalPart && a.UniqueID.endsWith(`\\${uniqueId}`) && partId === a.LogicalPartID);
                }
                for (const candidate of matchingAnnotations) {
                    if (candidate.DocumentName !== sheet.filename || candidate.ChannelName !== sheet.roomName) {
                        continue;
                    }
                    this.canClaimPhysicalAnnotation(completeUniqueId, candidate, logicalPart, partId, true);
                }
                if (matchingAnnotations.length > 1) {
                    this.model.warnings.push(`No annotation found for part ${logicalPart} with unique ID ${completeUniqueId} in document ${sheet.filename} (${sheet.uniqueId}). Found ${matchingAnnotations.length} matches without document or channel name match.`);
                    return undefined;
                }
            }
            if (matchingAnnotations.length > 0) {
                this.model.warnings.push(`No annotation found for part ${logicalPart} with unique ID ${completeUniqueId} in document ${sheet.filename} (${sheet.uniqueId}). Trying fallback.`);
            }
        }
        const indexedCandidates = matchingAnnotations.filter((annotation) => annotation._Index !== undefined);
        const indexedAnnotations = indexedCandidates.filter((annotation) => this.matchesPartIdentity(annotation, sheet, logicalPart, partId, completeUniqueId));
        const rejectedIndexedAnnotations = indexedCandidates.filter((annotation) => !indexedAnnotations.includes(annotation));
        if (rejectedIndexedAnnotations.length > 0) {
            this.recordRejectedIndexedAnnotationIdentityMismatch(rejectedIndexedAnnotations, logicalPart, completeUniqueId, sheet.filename);
        }
        const indexlessAnnotations = matchingAnnotations.filter((annotation) => annotation._Index === undefined);
        if (indexlessAnnotations.length > 0) {
            const matchingIndexlessAnnotations = indexlessAnnotations.filter((annotation) => this.matchesCompletePartIdentity(annotation, sheet, logicalPart, partId, completeUniqueId));
            if (matchingIndexlessAnnotations.length !== 1) {
                this.model.warnings.push(`Cannot resolve indexless annotation for part ${logicalPart} with unique ID ${uniqueId}. Found ${matchingIndexlessAnnotations.length} full-identity matches.`);
            }
            if (matchingIndexlessAnnotations.length > 0 && indexedAnnotations.length > 0) {
                this.model.warnings.push(`Conflicting indexed and indexless annotations for part ${logicalPart} with unique ID ${uniqueId}. Indexed physical designators: ${indexedAnnotations.map((annotation) => annotation.PhysicalDesignator).join(', ')}; indexless physical designators: ${matchingIndexlessAnnotations.map((annotation) => annotation.PhysicalDesignator).join(', ')}.`);
                return undefined;
            }
            matchingAnnotations = [
                ...indexedAnnotations,
                ...(matchingIndexlessAnnotations.length === 1 ? matchingIndexlessAnnotations : []),
            ];
        }
        else {
            matchingAnnotations = indexedAnnotations;
        }
        if (matchingAnnotations.length > 1) {
            this.model.warnings.push(`Multiple annotations found for part ${logicalPart} with unique ID ${uniqueId}. No annotation selected.`);
            return undefined;
        }
        if (matchingAnnotations?.length) {
            const useAnnotation = matchingAnnotations[0];
            if (!this.canClaimPhysicalAnnotation(completeUniqueId, useAnnotation, logicalPart, partId)) {
                return undefined;
            }
            this.reportLogicalLabelDifference(useAnnotation, logicalPart, completeUniqueId);
            this.claimedAnnotations.set(useAnnotation, sigma_engine_shared_2.Utils.resolveThunkMap(variables));
            this.uniqueIdClaimedAnnotations.set(completeUniqueId, useAnnotation);
            return useAnnotation;
        }
        return undefined;
    }
    recordRejectedIndexedAnnotationIdentityMismatch(rejectedAnnotations, logicalPart, completeUniqueId, documentName) {
        const initialCount = this.rejectedIndexedAnnotationIdentityMismatches.size;
        for (const annotation of rejectedAnnotations) {
            this.rejectedIndexedAnnotationIdentityMismatches.add(JSON.stringify([annotation._Index, annotation.DocumentName, annotation.UniqueID, annotation.LogicalPartID]));
        }
        if (this.rejectedIndexedAnnotationIdentityMismatches.size === initialCount) {
            return;
        }
        if (this.rejectedIndexedAnnotationIdentityMismatchExamples.length < 3) {
            this.rejectedIndexedAnnotationIdentityMismatchExamples.push(`part ${this.truncateDiagnosticValue(logicalPart ?? '')} with unique ID ${this.truncateDiagnosticValue(completeUniqueId)} in document ${this.truncateDiagnosticValue(documentName)}`);
        }
        const message = `Rejected indexed annotation identity mismatch; ${this.rejectedIndexedAnnotationIdentityMismatches.size} record(s) rejected. ` +
            `Examples: ${this.rejectedIndexedAnnotationIdentityMismatchExamples.join('; ')}`;
        if (this.rejectedIndexedAnnotationIdentityMismatchResult) {
            this.rejectedIndexedAnnotationIdentityMismatchResult.message = message;
            if (this.rejectedIndexedAnnotationIdentityMismatchWarningIndex !== undefined) {
                this.model.warnings[this.rejectedIndexedAnnotationIdentityMismatchWarningIndex] = message;
            }
            return;
        }
        this.rejectedIndexedAnnotationIdentityMismatchResult = {
            type: 'altium-name-generator',
            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            message,
        };
        this.recordProcessingResult(this.rejectedIndexedAnnotationIdentityMismatchResult);
        this.rejectedIndexedAnnotationIdentityMismatchWarningIndex = this.model.warnings.length;
        this.model.warnings.push(message);
    }
    truncateDiagnosticValue(value) {
        const maximumLength = 96;
        return value.length <= maximumLength ? value : `${value.slice(0, maximumLength - 1)}…`;
    }
    reportLogicalLabelDifference(annotation, logicalPart, completeUniqueId) {
        if (annotation.LogicalDesignator !== logicalPart) {
            this.model.warnings.push(`Saved logical designator ${annotation.LogicalDesignator} differs from ${logicalPart} for unique ID ${completeUniqueId}; resolved by exact saved instance identity.`);
        }
    }
    matchesPartIdentity(annotation, sheet, logicalPart, partId, completeUniqueId) {
        return ((annotation._Index !== undefined || annotation.LogicalDesignator === logicalPart) &&
            annotation.LogicalPartID === partId &&
            annotation.DocumentName === sheet.filename &&
            annotation.ChannelName === sheet.roomName &&
            annotation.UniqueID === completeUniqueId);
    }
    matchesCompletePartIdentity(annotation, sheet, logicalPart, partId, completeUniqueId) {
        return (annotation.LogicalDesignator === logicalPart &&
            annotation.LogicalPartID === partId &&
            annotation.DocumentName === sheet.filename &&
            annotation.ChannelName === sheet.roomName &&
            annotation.UniqueID === completeUniqueId);
    }
    canClaimPhysicalAnnotation(forModuleRef, annotation, logicalPart, partId, isReservation = false) {
        const claims = this.physicalAnnotationClaims.get(annotation.PhysicalDesignator) ?? new Map();
        const claimKey = JSON.stringify([annotation.DocumentName, annotation.ChannelName, forModuleRef, partId]);
        claims.set(claimKey, {
            logicalPart,
            partId,
            moduleRef: forModuleRef,
            documentName: annotation.DocumentName,
            channelName: annotation.ChannelName,
            isReservation,
        });
        this.physicalAnnotationClaims.set(annotation.PhysicalDesignator, claims);
        const conflictingClaims = Array.from(claims).filter(([key, claim]) => key !== claimKey &&
            (!(isReservation || claim.isReservation) ||
                (claim.documentName === annotation.DocumentName && claim.channelName === annotation.ChannelName)) &&
            (claim.partId === partId || claim.logicalPart !== logicalPart));
        const ambiguousClaims = this.ambiguousPhysicalAnnotationClaims.get(annotation.PhysicalDesignator) ?? new Set();
        if (conflictingClaims.length > 0) {
            ambiguousClaims.add(claimKey);
            for (const [key] of conflictingClaims) {
                ambiguousClaims.add(key);
            }
            this.ambiguousPhysicalAnnotationClaims.set(annotation.PhysicalDesignator, ambiguousClaims);
        }
        if (ambiguousClaims.has(claimKey)) {
            const rejectedClaims = Array.from(claims).filter(([key]) => ambiguousClaims.has(key));
            for (const [, { moduleRef: ref }] of rejectedClaims) {
                this.uniqueIdClaimedAnnotations.delete(ref);
                const name = this.refToNameMap.get(ref);
                if (name?.physical === annotation.PhysicalDesignator) {
                    if (this.physicalNameToRefMap.get(annotation.PhysicalDesignator) === name) {
                        this.physicalNameToRefMap.delete(annotation.PhysicalDesignator);
                    }
                    this.refToNameMap.delete(ref);
                }
            }
            this.model.warnings.push(`Conflicting physical annotation ${annotation.PhysicalDesignator}; claimants: ${rejectedClaims.map(([, claim]) => `${claim.logicalPart} (${claim.moduleRef}, part ${claim.partId}, document ${claim.documentName}, channel ${claim.channelName})`).join('; ')}. No saved mapping selected.`);
            return false;
        }
        if (isReservation) {
            return true;
        }
        const existingName = this.physicalNameToRefMap.get(annotation.PhysicalDesignator);
        if (existingName?.logicalMultiPart.length &&
            existingName.logical === logicalPart &&
            existingName.physical === annotation.PhysicalDesignator) {
            return true;
        }
        const existingRef = Array.from(this.refToNameMap.entries()).find(([, name]) => name === existingName)?.[0];
        if (existingRef !== undefined && existingRef !== forModuleRef) {
            this.model.warnings.push(`Physical annotation ${annotation.PhysicalDesignator} is already claimed by ${existingRef}; rejected claimant ${forModuleRef}.`);
            return false;
        }
        return true;
    }
    generateName(oldName, sheet, variables, annotationSelectionMode, designatorType, nameType) {
        const variablesThunkMap = this.initVariableThunkMap(sheet, oldName, variables);
        if (nameType === NameGeneratorNameType.Module) {
            const name = this.getAnnotationsForModule(oldName, sheet, variables, designatorType, true);
            if (name) {
                return name;
            }
            if (annotationSelectionMode === NameGeneratorAnnotationSelectionMode.ExplicitOnly) {
                return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
                    logical: oldName,
                    physical: oldName,
                    physicalInSchematic: oldName,
                    isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
                }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
            }
            if (annotationSelectionMode === NameGeneratorAnnotationSelectionMode.ExplicitFirstUnconsumedChannel) {
                if (this.canUseComponentPhysicalName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), oldName, designatorType)) {
                    return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
                        logical: oldName,
                        physical: oldName,
                        physicalInSchematic: oldName,
                        isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
                    }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
                }
            }
        }
        else {
            if (annotationSelectionMode === NameGeneratorAnnotationSelectionMode.ExplicitFirstUnconsumedChannel ||
                annotationSelectionMode === NameGeneratorAnnotationSelectionMode.ExplicitOnly) {
                throw new Error('ExplicitFirstUnconsumedChannel and ExplicitOnly are only supported for module names, not nets');
            }
        }
        if (sheet.numInstances === 1) {
            const effectiveTotalInstances = sheet.designWideInstances ?? sheet.totalSheetInstances;
            if (nameType === NameGeneratorNameType.Module) {
                if (effectiveTotalInstances === 1) {
                    if (nameType === NameGeneratorNameType.Module) {
                        return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
                            logical: oldName,
                            physical: oldName,
                            physicalInSchematic: oldName,
                            isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
                        }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
                    }
                }
            }
            if (nameType === NameGeneratorNameType.Net) {
                if (effectiveTotalInstances === 1) {
                    return oldName;
                }
            }
        }
        const name = this.annotateReferenceDesignatorUsingChannelDesignatorFormatString(oldName, variablesThunkMap);
        if (nameType === NameGeneratorNameType.Module) {
            return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
                logical: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'Component', true),
                physical: name,
                physicalInSchematic: name,
                isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
            }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
        }
        return name;
    }
    getAnnotationsForModule(designator, sheet, variables, designatorType, enableFallbacks) {
        const variablesThunkMap = this.initVariableThunkMap(sheet, designator, variables);
        let name;
        const annotation = this.getAnnotationEntryForPart(sheet, variablesThunkMap, enableFallbacks);
        if (annotation) {
            return this.selectNameType(this.registerComponentName(sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PathUniqueID', true), {
                logical: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'Component', true),
                physical: annotation.PhysicalDesignator,
                physicalInSchematic: annotation.PhysicalDesignator,
                isPhysicalPart: sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'IsPhysicalPart', true),
            }, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartCount', true), sheet.filename), designatorType, sigma_engine_shared_2.Utils.resolveValueFromThunkMap(variablesThunkMap, 'PartId', true));
        }
        return name;
    }
    initVariableThunkMap(sheet, oldName, variables) {
        return {
            DocumentName: () => sheet.name,
            ChannelAlpha: () => {
                if (typeof sheet?.channelAlpha !== 'undefined') {
                    return sheet.channelAlpha.toString();
                }
                return channelAlphaSuffix(sheet.physicalChannelIndex);
            },
            ChannelIndex: () => {
                return sheet.physicalChannelIndex.toString();
            },
            ChannelPrefix: () => {
                if (typeof sheet?.channelPrefix !== 'undefined') {
                    return sheet.channelPrefix.toString();
                }
                return sheet.name;
            },
            RoomName: () => {
                if (typeof sheet?.roomName !== 'undefined') {
                    return sheet.roomName.toString();
                }
                const warning = `Could not determine $RoomName when re-annotating: ChannelRoomNamingStyle is not implemented.`;
                if (!this.model.warnings.includes(warning)) {
                    this.model.warnings.push(warning);
                }
                return sheet.name;
            },
            Component: oldName,
            ...(variables ?? {}),
        };
    }
    selectNameType(name, designatorType, partId) {
        const useId = partId - 1;
        switch (designatorType) {
            case NameGeneratorDesignatorType.Logical:
                if (name.logicalMultiPart.length > 0) {
                    if (useId >= name.logicalMultiPart.length) {
                        throw new Error(`Part ID ${partId} is out of range for multi-part component ${name.logical} (${name.logicalMultiPart.join(', ')})`);
                    }
                    return name.logicalMultiPart[useId];
                }
                return name.logical;
            case NameGeneratorDesignatorType.Physical:
                return name.physical;
            case NameGeneratorDesignatorType.PhysicalInSchematic:
                if (name.physicalInSchematicMultiPart.length > 0) {
                    if (useId >= name.physicalInSchematicMultiPart.length) {
                        throw new Error(`Part ID ${partId} is out of range for multi-part component ${name.physicalInSchematic} (${name.physicalInSchematicMultiPart.join(', ')})`);
                    }
                    return name.physicalInSchematicMultiPart[useId];
                }
                return name.physicalInSchematic;
        }
    }
    generatePowerPortName(powerPort) {
        const paddedIndex = powerPort.index.toString().padStart(3, '0');
        return `#PWR_${powerPort.text}_${paddedIndex}`;
    }
    canUseComponentPhysicalName(forModuleRef, name, nameType) {
        if (!this.physicalNameToRefMap.has(name)) {
            return true;
        }
        let useValue;
        switch (nameType) {
            case NameGeneratorDesignatorType.Logical:
                useValue = this.physicalNameToRefMap.get(name)?.physical;
                break;
            case NameGeneratorDesignatorType.Physical:
                useValue = this.physicalNameToRefMap.get(name)?.physical;
                break;
            case NameGeneratorDesignatorType.PhysicalInSchematic:
                useValue = this.physicalNameToRefMap.get(name)?.physicalInSchematic;
                break;
        }
        return useValue === forModuleRef;
    }
    componentRefHasName(ref) {
        return this.refToNameMap.has(ref);
    }
    getComponentNameFromRef(ref) {
        return this.refToNameMap.get(ref);
    }
    getComponentRefFromName(name) {
        return this.physicalNameToRefMap.get(name);
    }
    registerComponentName(ref, inputName, partCount, sourceDocument) {
        const name = {
            ...inputName,
            logicalMultiPart: this.generateMultiPartNames(inputName.logical, partCount),
            physicalInSchematicMultiPart: this.generateMultiPartNames(inputName.physicalInSchematic, partCount),
        };
        let existing = this.refToNameMap.get(ref);
        if (existing) {
            if (_.isEqual(existing, name)) {
                return name;
            }
            throw new Error(`Component ref ${ref} already has a name: ${this.refToNameMap.get(ref)?.physical}`);
        }
        if (name.isPhysicalPart) {
            existing = this.physicalNameToRefMap.get(name.physical);
            if (existing) {
                if (existing.logicalMultiPart.length > 0 && _.isEqual(existing, name)) {
                    return name;
                }
                this.recordProcessingResult({
                    type: 'altium-name-generator',
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    message: `Duplicate component designator ${name.physical}${sourceDocument ? ` in ${sourceDocument}` : ''}`,
                });
            }
            else {
                this.physicalNameToRefMap.set(name.physical, name);
            }
        }
        this.refToNameMap.set(ref, name);
        return name;
    }
    recordProcessingResult(result) {
        this.processingResults.push(result);
        this.processingResultSink?.(result);
    }
    generateMultiPartNames(logical, partCount) {
        if (partCount <= 1) {
            return [];
        }
        const names = [];
        for (let i = 0; i < partCount; i++) {
            names.push(`${logical}${String.fromCharCode('A'.charCodeAt(0) + i)}`);
        }
        return names;
    }
    annotateReferenceDesignatorUsingChannelDesignatorFormatString(component, args) {
        const originalComponent = sigma_engine_shared_2.Utils.resolveValueFromThunkMap(args, 'Component') ?? component;
        const diffPairSuffix = this.extractDifferentialPairSuffix(originalComponent);
        const componentWithoutDiffPairSuffix = diffPairSuffix ?
            originalComponent.substring(0, originalComponent.length - diffPairSuffix.length)
            : originalComponent;
        const myArgs = {
            ComponentPrefix: () => /^([^0-9_-]+)/.exec(componentWithoutDiffPairSuffix)?.[1],
            ComponentIndex: () => /([0-9]+)[A-Za-z]*$/.exec(componentWithoutDiffPairSuffix)?.[1],
            ...args,
            Component: componentWithoutDiffPairSuffix,
        };
        const formatString = this.projectConfigUtil.getDesignConfiguration('ChannelDesignatorFormatString');
        const replaceFn = (key) => {
            return this.resolveOrError(myArgs, key);
        };
        return (formatString
            .replace(/\$RoomName/g, replaceFn)
            .replace(/\$ChannelAlpha/g, replaceFn)
            .replace(/\$ChannelPrefix/g, replaceFn)
            .replace(/\$ChannelIndex/g, replaceFn)
            .replace(/\$ComponentPrefix/g, replaceFn)
            .replace(/\$ComponentIndex/g, replaceFn)
            .replace(/\$Component/g, replaceFn) + diffPairSuffix);
    }
    extractDifferentialPairSuffix(netName) {
        const suffix = this.projectConfigUtil
            .getDifferentialPairSuffixes()
            .find((candidate) => candidate.startsWith('_') && netName.toLowerCase().endsWith(candidate.toLowerCase()));
        return suffix === undefined ? '' : netName.slice(-suffix.length);
    }
    resolveOrError(args, key) {
        if (key.startsWith('$')) {
            key = key.substring(1);
        }
        return key in args ?
            (sigma_engine_shared_2.Utils.resolveValueFromThunkMap(args, key) ?? `{ERROR:${key}NotProvided}`)?.toString()
            : `{ERROR:${key}NotProvided}`;
    }
}
exports.AltiumNameGenerator = AltiumNameGenerator;
//# sourceMappingURL=AltiumNameGenerator.js.map